/**
 * Centralized logger for Admin Panel
 * 
 * Usage:
 *   import logger from './utils/logger';
 *   logger.info('Hello', { key: 'value' });
 *   logger.error('Error occurred', error);
 * 
 * Toggle backends via config or env.
 */

// Backend registry
const backends = {
  console: null,  // will be populated by consoleForwarder if enabled
  native: {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
  }
};

let activeBackend = 'native'; // default to native console

/**
 * Register a logging backend (e.g., 'console' with forwarding enabled)
 * @param {string} name - Backend name
 * @param {object} impl - Implementation with log/info/warn/error methods
 */
export function registerBackend(name, impl) {
  backends[name] = impl;
}

/**
 * Set the active backend
 * @param {string} name - Backend name ('native', 'console', etc.)
 */
export function setBackend(name) {
  if (backends[name]) {
    activeBackend = name;
  } else {
    console.warn(`[Logger] Backend "${name}" not found, keeping "${activeBackend}"`);
  }
}

/**
 * Get current backend name
 */
export function getBackend() {
  return activeBackend;
}

const logger = {
  log: (...args) => {
    const backend = backends[activeBackend] || backends.native;
    backend.log(...args);
  },
  info: (...args) => {
    const backend = backends[activeBackend] || backends.native;
    backend.info(...args);
  },
  warn: (...args) => {
    const backend = backends[activeBackend] || backends.native;
    backend.warn(...args);
  },
  error: (...args) => {
    const backend = backends[activeBackend] || backends.native;
    backend.error(...args);
  },
  debug: (...args) => {
    // Debug always goes to native console (not forwarded)
    backends.native.log('[DEBUG]', ...args);
  },
};

export default logger;

